/* eslint-disable prefer-destructuring */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/prefer-stateless-function, react/no-multi-comp */
import React, { createRef, ComponentType } from 'react';
import produce from 'immer';
import _ from 'lodash';

import {
	COMPONENT_STATES,
	TRANSITION_TYPE,
	COMPONENT_TYPE,
	ANIMATION_STATE,
	ANIMATION_TRIGGER,
	HAS_ANSWERS_ATTR,
} from 'common/constants';
import { isLayerType } from 'common/utils/is-layer-type';
import { BBUiConfig, BBEditableModeProps, BBSymbolInfo, BBCommonProps, ComponentTypes } from 'types/story';
// eslint-disable-next-line max-len
import { AnimationSettingsEvent } from 'admin/components/pages/Story/CardEditor/Inspector/AnimationSettings/TriggerSettings';
import { location } from 'common/utils/url-helper';
import { StateDOMAttrubutesManager } from 'client/components/common/BuildingBlocks/StateDOMAttrubutesManager';
import { CardTransitionContext } from 'client/components/common/CardTransition';
import { TransitionHandlerContextType } from 'client/components/common/CardTransition/TransitionHandler';
import { ANIMATION_HOC_SENDER_ID, ANIMATION_SETTINGS_SENDER_ID } from 'client/constants/common';
import SelectedComponentWatcher from 'client/components/common/SelectionHint/SelectedComponentWatcher';

import transitionSCSS from 'client/components/common/BuildingBlocks/Transition.scss';
import cardCSS from 'client/components/common/BuildingBlocks/Card/Card.scss';
import contentCSS from 'client/components/common/BuildingBlocks/Content/Content.scss';
import floatAboveCSS from 'client/components/common/BuildingBlocks/FloatAbove/FloatAbove.scss';

import { rafTicker, parseAnimation, AnimationData } from './animation';
import './BuildingBlock.scss';

// withCardTransitionContext > withExpose > withBuildingBlock > withAnimation > component

const TRANSITION_PRESET = {
	common: {
		[TRANSITION_TYPE.NONE]: {
			base: transitionSCSS.defaultBase,
			in: transitionSCSS.defaultIn,
			out: transitionSCSS.defaultOut,
		},
		[TRANSITION_TYPE.FADE]: {
			base: transitionSCSS.fadeBase,
			in: transitionSCSS.fadeIn,
			out: transitionSCSS.fadeOut,
		},
		[TRANSITION_TYPE.ZOOM]: {
			base: transitionSCSS.zoomBase,
			in: transitionSCSS.zoomIn,
			out: transitionSCSS.zoomOut,
		},
	},
	[COMPONENT_TYPE.CARD]: {
		[TRANSITION_TYPE.ZOOM]: {
			base: cardCSS.zoomBase,
			in: cardCSS.zoomIn,
			out: cardCSS.zoomOut,
		},
	},
	[COMPONENT_TYPE.CONTENT]: {
		[TRANSITION_TYPE.FADE]: {
			base: contentCSS.fadeBase,
			in: contentCSS.fadeIn,
			out: contentCSS.fadeOut,
		},
	},
	[COMPONENT_TYPE.FLOAT_ABOVE]: {
		[TRANSITION_TYPE.FADE]: {
			base: floatAboveCSS.fadeBase,
			in: floatAboveCSS.fadeIn,
			out: floatAboveCSS.fadeOut,
		},
		[TRANSITION_TYPE.ZOOM]: {
			base: floatAboveCSS.zoomBase,
			in: floatAboveCSS.zoomIn,
			out: floatAboveCSS.zoomOut,
		},
	},
	[COMPONENT_TYPE.FLOAT_BELOW]: {
		[TRANSITION_TYPE.FADE]: {
			base: floatAboveCSS.fadeBase,
			in: floatAboveCSS.fadeIn,
			out: floatAboveCSS.fadeOut,
		},
		[TRANSITION_TYPE.ZOOM]: {
			base: floatAboveCSS.zoomBase,
			in: floatAboveCSS.zoomIn,
			out: floatAboveCSS.zoomOut,
		},
	},
};

function isEditableMode(componentProps) {
	return !_.isEmpty(componentProps.editableModeProps);
}

function camelCaseStr(str: string) {
	if (str.includes('-')) {
		const arr = str.split('-');
		const capital = arr.map((item, index) =>
			index ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase() : item.toLowerCase()
		);

		return capital.join('');
	}

	return str;
}

/**
 * HOC with basic functionality for all BuildingBlocks
 * @param {*} Component
 */
export const withBuildingBlock = Component => {
	const Block = withAnimation(Component);

	type Props = {
		type: string;
		uiConfig: BBUiConfig;
		children: ComponentType | ComponentType[];
		editableModeProps?: BBEditableModeProps;
		parentStates?: BBCommonProps['states'];
	};

	type State = {
		states: BBCommonProps['states'];
		parentStates?: BBCommonProps['states'];
	};

	return class WithBuildingBlock extends React.Component<Props, State> {
		static displayName = `withBuildingBlock(${Component.displayName || Component.name})`;

		static defaultProps = {
			editableModeProps: {},
			children: null,
		};

		constructor(props: Props, context: any) {
			super(props, context);

			this.onEditableMouseEnter = this.onEditableMouseEnter.bind(this);
			this.onEditableMouseLeave = this.onEditableMouseLeave.bind(this);
			this.onEditableMouseDown = this.onEditableMouseDown.bind(this);
			this.onEditableFocus = this.onEditableFocus.bind(this);
			this.onEditableMouseDownCapture = this.onEditableMouseDownCapture.bind(this);
			this.onDoubleClickCapture = this.onDoubleClickCapture.bind(this);

			// building block current states (hover, correct, etc.)
			const initialStates: State['states'] = _.reduce(
				_.omit(COMPONENT_STATES, ['DEFAULT']),
				(memo, value, key) => _.set(memo, value, false),
				{}
			);

			this.state = {
				states: { ...(props.parentStates ?? {}), ...initialStates },
				parentStates: props.parentStates,
			};
		}

		static getDerivedStateFromProps({ parentStates }: Props, state: State) {
			// Update states with a parentStates
			if (parentStates && parentStates !== state.parentStates) {
				return { states: { ...state.states, ...parentStates }, parentStates };
			}
			return null;
		}

		onEditableMouseEnter(event) {
			_.invoke(this.props.editableModeProps, 'eventListeners.onMouseEnter', event, this.props);
		}

		onEditableMouseLeave(event) {
			_.invoke(this.props.editableModeProps, 'eventListeners.onMouseLeave', event);
		}

		onEditableMouseDown(event) {
			_.invoke(this.props.editableModeProps, 'eventListeners.onMouseDown', event, this.props);
		}

		onEditableFocus(event) {
			_.invoke(this.props.editableModeProps, 'eventListeners.onFocus', event, this.props);
		}

		onEditableMouseDownCapture(event) {
			_.invoke(this.props.editableModeProps, 'eventListeners.onMouseDownCapture', event, this.props);
		}

		onDoubleClickCapture(event) {
			_.invoke(this.props.editableModeProps, 'eventListeners.onDoubleClickCapture', event, this.props);
		}

		/**
		 * NOTE:
		 *   Unfortunately changing state does't update this.props.uiConfig.componentProps and after state changed
		 *   component will still receive computed props as it in default state.
		 *   This becomes a problem when some component inside of own code depends on "uiConfig.componentProps",
		 *   which are won't re-compute on state change.
		 *   (!) Think about, how to re-compute props for a component after his state is changed.
		 * @param states {Object}
		 */
		setStates = states => {
			this.setState(prevState => ({ states: { ...prevState.states, ...states } }));
		};

		/**
		 * this.state.states converted as data attributes to assign onto DOM node
		 * @return {*}
		 */
		get stateAttrs() {
			if (_.isEmpty(this.state.states)) {
				console.error(`<${this.constructor.name} /> has no states assigned!`);
			}
			const statesManager = new StateDOMAttrubutesManager(this.state.states);
			return statesManager.attributes;
		}

		get eventListeners() {
			if (this.isEditableMode) {
				if (this.props.uiConfig.editorProps.selectable) {
					return {
						onMouseEnter: this.onEditableMouseEnter,
						onMouseLeave: this.onEditableMouseLeave,
						onMouseDown: this.onEditableMouseDown,
						onFocus: this.onEditableFocus,
						onMouseDownCapture: this.onEditableMouseDownCapture,
						onDoubleClickCapture: this.onDoubleClickCapture,
					};
				}
				return {};
			}

			const { HOVER, SELECTED, SELECTED_HOVER } = COMPONENT_STATES;

			// Non-editor listeners
			return {
				onMouseEnter: event => {
					const hasAnswers = !!event.target.closest(`[${HAS_ANSWERS_ATTR}="true"]`);

					this.setState(
						produce(draft => {
							if (hasAnswers) {
								return;
							}

							_.set(draft, ['states', HOVER], true);
							_.set(draft, ['states', SELECTED_HOVER], draft.states[SELECTED]);
						})
					);
				},
				onMouseLeave: e => {
					this.setState(
						produce(draft => {
							_.set(draft, ['states', HOVER], false);
							_.set(draft, ['states', SELECTED_HOVER], false);
						})
					);
				},
			};
		}

		get isEditableMode() {
			return isEditableMode(this.props);
		}

		render() {
			const bbProps = {
				states: this.state.states,
				setStates: this.setStates,
				stateAttrs: this.stateAttrs,
				eventListeners: this.eventListeners,
				isEditableMode: this.isEditableMode,
			};

			// @ts-expect-error ts-migrate FIXME
			return <Block {...this.props} {...bbProps} />;
		}
	};
};

/**
 * HOC for animation functionality on BuildingBlocks
 * Animation data is parsed to regular css-animation keyframes,
 * which are appended to <style> next-to Component's resulting element. When animation is triggered, this HOC changes
 * props.uiConfig.nodeProps.style to start normal css-animation on Component's element.
 * For more details on animation concept see ./animation.js - parseAnimation description
 * Assumes Component attaches ref={props.containerRef} to its root HTMLElement
 *
 * @param {*} Component
 */
export const withAnimation = Component => {
	type Props = BBCommonProps & { animation?: Record<string, any> };

	type AnimationDataRecord = Record<string, AnimationData>;

	type State = {
		currentAnimation: string;
		animationState: Record<string, typeof ANIMATION_STATE[keyof typeof ANIMATION_STATE]>;
		animationDataRaw: AnimationData;
		animationData: AnimationDataRecord;
	};

	return class WithAnimation extends React.Component<Props, State> {
		static displayName = `withAnimation(${Component.displayName || Component.name})`;

		static defaultProps = {
			animation: null,
			eventListeners: undefined,
			editableModeProps: {},
		};

		static getDerivedStateFromProps(props: Props, state: State) {
			const animation = _.get(props, 'uiConfig.componentProps.animation');
			if (animation !== state.animationDataRaw) {
				return {
					animationDataRaw: animation,
					animationData: _.reduce(
						animation,
						(acc, current: AnimationData, key: string) => {
							acc[key] = parseAnimation(current, `animation-${key}-${props._id}`);
							return acc;
						},
						{}
					),
				};
			}
			return null;
		}

		containerRef = createRef<HTMLElement>();

		delayedEventTimeouts: any;

		isEmbed: boolean;

		constructor(props: Props) {
			super(props);

			this.delayedEventTimeouts = {};
			const animationDataRaw = _.get(props, 'uiConfig.componentProps.animation');
			const animationData = _.reduce(
				animationDataRaw,
				(acc, current: AnimationData, key: string) => {
					acc[key] = parseAnimation(current, `animation-${key}-${props._id}`);
					return acc;
				},
				{}
			);

			this.isEmbed =
				location.client.isEmbedDevPreview ||
				(window.parent !== window && !this.isEditableMode && !location.client.isPreviewRoute);

			const defaultTrigger = [
				ANIMATION_TRIGGER.ON_LOAD,
				ANIMATION_TRIGGER.ON_CARD_EXPOSE_START,
				ANIMATION_TRIGGER.ON_CARD_EXPOSE_COMPLETE,
				ANIMATION_TRIGGER.ON_VIEWPORT,
				ANIMATION_TRIGGER.ON_HOVER,
				ANIMATION_TRIGGER.ON_CLICK,
				// @ts-expect-error ts-migrate FIXME
			].filter(trigger => !!_.find(animationData, { trigger }))[0];

			const currentAnimation = defaultTrigger
				? // @ts-expect-error ts-migrate FIXME
				  _.find(animationData, { trigger: defaultTrigger }).id
				: Object.keys(animationData || {})[0];

			this.state = {
				currentAnimation: this.isEditableMode ? '' : currentAnimation,
				animationDataRaw, // eslint-disable-line react/no-unused-state
				animationData,
				animationState: _.reduce(
					animationData,
					(acc, data, key) => {
						acc[key] = data && !this.isEditableMode ? ANIMATION_STATE.NOT_STARTED : ANIMATION_STATE.NONE;
						return acc;
					},
					{}
				),
			};
		}

		componentDidMount() {
			if (!this.isEditableMode && this.animationData) {
				_.forEach(this.animationData as AnimationDataRecord, (animationData, triggerId) => {
					const { trigger } = animationData;
					const isCustomTrigger = !_.values(ANIMATION_TRIGGER).some(t => t === animationData.trigger);

					if (trigger) {
						if (trigger === ANIMATION_TRIGGER.ON_LOAD) {
							this.startAnimation(triggerId);
						}
						if (trigger === ANIMATION_TRIGGER.ON_VIEWPORT) {
							if (this.containerRef.current) {
								// listen
								this.scrollListeners.add();

								// handle case when initially "in view", but not for embed, because of sdk calling it
								if (!this.isEmbed) {
									this.onWindowScroll();
								}
							} else {
								const componentType = (this.props as any).type;
								throw new Error(
									`withAnimation HOC — wrapped ${componentType} refferecne error.
								${componentType} should attach 'props.containerRef' to its root HTMLElement`
								);
							}
						}
						if (
							isCustomTrigger ||
							trigger === ANIMATION_TRIGGER.ON_CARD_EXPOSE_START ||
							trigger === ANIMATION_TRIGGER.ON_CARD_EXPOSE_COMPLETE
						) {
							if (trigger === triggerId) return;

							window.addEventListener(trigger, () => this.startAnimation(triggerId));
						}
					}
				});
			}

			// receiver message from Editor (run animation preview)
			window.addEventListener('message', this.onWindowMessage);
		}

		componentWillUnmount() {
			_.forEach(this.delayedEventTimeouts, timeouts => {
				_.forEach(timeouts, timeoutID => rafTicker.clearTimeout(timeoutID));
			});

			this.scrollListeners.remove();
			window.removeEventListener('message', this.onWindowMessage);

			_.forEach(this.animationData, animationData => {
				if (_.get(animationData, 'trigger')) {
					window.removeEventListener<any>(animationData?.trigger, this.startAnimation);
				}
			});
		}

		get animationData() {
			return this.state.animationData;
		}

		get isEditableMode() {
			return isEditableMode(this.props);
		}

		get scrollListeners() {
			const events = ['scroll', 'pageScroll'];
			return {
				add: () => events.forEach(ev => window.addEventListener(ev, this.onWindowScroll)),
				remove: () => events.forEach(ev => window.removeEventListener(ev, this.onWindowScroll)),
			};
		}

		getCurrentAnimationStyles = () => {
			const { currentAnimation, animationState } = this.state;
			const animationData: AnimationData | undefined = this.animationData[currentAnimation];
			let style: { [key: string]: string } | undefined;

			if (!animationData) {
				return style;
			}

			const { name, reverse, duration, loop, sortedKeyframes } = animationData;

			switch (animationState[currentAnimation]) {
				case ANIMATION_STATE.NOT_STARTED:
					style = _.first(sortedKeyframes)?.[1];
					break;
				case ANIMATION_STATE.PLAYING: // const animationData = animationData;
					style = {
						..._.first(sortedKeyframes)?.[1],
						animationName: name,
						animationDuration: `${parseFloat(duration) * (reverse ? 0.5 : 1)}ms`,
						animationTimingFunction: 'linear', // it should always be linear
						animationFillMode: 'both',
						animationIterationCount: `${loop ? Number.MAX_VALUE : reverse ? 2 : 1}`,
						animationDirection: reverse ? 'alternate' : 'normal',
					};
					break;
				case ANIMATION_STATE.COMPLETE:
					if (reverse) {
						style = _.first(sortedKeyframes)?.[1];
					} else {
						style = _.last(sortedKeyframes)?.[1];
					}
					break;
				case ANIMATION_STATE.NONE:
					style = {
						transform: '',
						opacity: '',
					};
					break;
				default:
					break;
			}

			if (style) {
				style = Object.entries(style).reduce((acc, [key, value]) => {
					acc[camelCaseStr(key)] = value;
					return acc;
				}, {});
			}

			return style;
		};

		getEventListeners = () => {
			const listeners: any = {};

			_.forEach(this.animationData as AnimationDataRecord, animationData => {
				if (animationData && !this.isEditableMode) {
					if (animationData.trigger === ANIMATION_TRIGGER.ON_CLICK) {
						listeners.onClick = this.onClick;
					}
					if (animationData.trigger === ANIMATION_TRIGGER.ON_HOVER) {
						listeners.onMouseEnter = this.onMouseEnter;
						listeners.onMouseLeave = this.onMouseLeave;
					}
				}
			});

			return listeners;
		};

		startAnimation = async (trigger: string, p = {}) => {
			const animationState = this.state.animationState[trigger];
			const triggerName = this.animationData[trigger].trigger;
			const { ON_CARD_EXPOSE_START, ON_CARD_EXPOSE_COMPLETE } = ANIMATION_TRIGGER;

			// Prevent call of expose animation triggers on card expose OUT
			if (
				(triggerName === ON_CARD_EXPOSE_START || triggerName === ON_CARD_EXPOSE_COMPLETE) &&
				(animationState === 'COMPLETE' || animationState === 'PLAYING')
			) {
				return;
			}

			const defaultParams: { mode: 'normal' | 'preview' } = { mode: 'normal' };
			const params = { ...defaultParams, ...p };

			if (!this.delayedEventTimeouts[trigger]) this.delayedEventTimeouts[trigger] = [];
			_.forEach(this.delayedEventTimeouts[trigger], timeoutID => rafTicker.clearTimeout(timeoutID));

			if (this.state.animationState[trigger] === ANIMATION_STATE.PLAYING) {
				await new Promise<void>(resolve =>
					this.setState(
						state => ({
							animationState: { ...state.animationState, [trigger]: ANIMATION_STATE.NOT_STARTED },
						}),
						resolve
					)
				);
			}

			this.setState({ currentAnimation: trigger }, () => {
				const delayTimeoutId = rafTicker.setTimeout(() => {
					_.forEach(this.animationData[trigger].events, event => {
						const timeoutID = rafTicker.setTimeout(() => {
							window.dispatchEvent(new Event(event.eventId));
						}, event.delay);
						this.delayedEventTimeouts[trigger].push(timeoutID);
					});

					this.setState(state => ({
						animationState: { ...state.animationState, [trigger]: ANIMATION_STATE.PLAYING },
					}));

					this.delayedEventTimeouts[trigger].push(
						rafTicker.setTimeout(() => {
							const animationData = this.animationData[trigger];
							const targetState =
								params.mode === 'normal'
									? animationData.loop
										? ANIMATION_STATE.PLAYING
										: ANIMATION_STATE.COMPLETE
									: ANIMATION_STATE.NONE;

							this.setState(
								state => ({
									animationState: { ...state.animationState, [trigger]: targetState },
								}),
								() => {
									window.parent.postMessage(
										{
											sender: ANIMATION_HOC_SENDER_ID,
											type: 'complete',
											trigger,
											elementId: this.props._id,
										} as AnimationSettingsEvent,
										'*'
									);
								}
							);
						}, +this.animationData[trigger].duration)
					);
				}, +this.animationData[trigger].delay);

				this.delayedEventTimeouts[trigger].push(delayTimeoutId);
			});
		};

		stopAnimation = (trigger: string) => {
			_.forEach(this.delayedEventTimeouts, timeouts => {
				_.forEach(timeouts, timeoutID => rafTicker.clearTimeout(timeoutID));
			});

			this.setState(state => ({
				animationState: { ...state.animationState, [trigger]: ANIMATION_STATE.NONE },
			}));
		};

		onWindowMessage = ({ data: e }: { data: AnimationSettingsEvent }) => {
			if (e.sender === ANIMATION_SETTINGS_SENDER_ID && e.elementId === this.props._id) {
				switch (e.type) {
					case 'start':
						this.startAnimation(e.trigger, { mode: 'preview' });
						break;

					case 'stop':
						this.stopAnimation(e.trigger);
						break;

					default:
						break;
				}
			}
		};

		onMouseEnter = event => {
			_.invoke(this.props, 'eventListeners.onMouseEnter', event);
			_.invoke(this.props, 'uiConfig.nodeProps.onMouseEnter', event);

			const animation = _.find(this.animationData as AnimationDataRecord, {
				trigger: ANIMATION_TRIGGER.ON_HOVER,
			});

			if (animation) {
				this.startAnimation(animation.id);
			}
		};

		onMouseLeave = event => {
			_.invoke(this.props, 'eventListeners.onMouseLeave', event);
			_.invoke(this.props, 'uiConfig.nodeProps.onMouseLeave', event);

			const animation = _.find(this.animationData as AnimationDataRecord, {
				trigger: ANIMATION_TRIGGER.ON_HOVER,
			});

			if (animation) {
				this.stopAnimation(animation.id);
			}
		};

		onClick = event => {
			_.invoke(this.props, 'eventListeners.onClick', event);
			_.invoke(this.props, 'uiConfig.nodeProps.onClick', event);

			const animation = _.find(this.animationData as AnimationDataRecord, {
				trigger: ANIMATION_TRIGGER.ON_CLICK,
			});

			if (animation) {
				this.startAnimation(animation.id);
			}
		};

		onWindowScroll = (e?: Event) => {
			const container = this.containerRef.current;

			if (container) {
				const vpH = this.isEmbed ? _.get(e, 'detail.viewport.h', window.innerHeight) : window.innerHeight;
				const bBox = container.getBoundingClientRect();
				const pagePosTop = _.get(e, 'detail.containerPosTop', 0);
				const controlPoint = pagePosTop + bBox.y + bBox.height;

				if (controlPoint > 0 && controlPoint < vpH) {
					const animation = _.find(this.animationData, { trigger: ANIMATION_TRIGGER.ON_VIEWPORT });

					if (animation) this.startAnimation(animation.id);

					this.scrollListeners.remove();
				}
			}
		};

		render() {
			const { currentAnimation } = this.state;
			const keyframes = _.get(this.animationData[currentAnimation], 'style');
			const style = this.getCurrentAnimationStyles();
			const eventListeners = this.getEventListeners();
			const bBProps = produce(
				{
					uiConfig: _.get(this.props, 'uiConfig'),
					eventListeners: _.merge({}, this.props.eventListeners, eventListeners),
				},
				draft => {
					_.set(
						draft,
						'uiConfig.nodeProps.style',
						_.merge({}, _.get(draft, 'uiConfig.nodeProps.style'), style)
					);
					_.set(draft, 'uiConfig.nodeProps.onClick', eventListeners.onClick);
					_.set(draft, 'uiConfig.nodeProps.onMouseEnter', eventListeners.onMouseEnter);
				}
			);

			return (
				<>
					{keyframes && <style>{keyframes}</style>}
					<Component {...this.props} {...bBProps} containerRef={this.containerRef} />
				</>
			);
		}
	};
};

/**
 * HOC for supporting CSSTransition between cards.
 * Each BuildingBlock receives callback which should be called when its transition finished.
 * Every parent creates its own one and passes to children,
 * only when all children finish their transitions — callback is called
 *
 * @param {*} Component
 */
export const withExpose = Component => {
	const Block = withBuildingBlock(Component);

	type Props = {
		type: string;
		in?: boolean;
		isCardTree?: boolean;
		exposeDuration?: number;
		exposeEffect?: string;
		children?: React.ReactNode;
		onTransitionEnd?: (...args: any[]) => any;
		editableModeProps?: BBEditableModeProps;
	};

	return class WithExpose extends React.Component<Props> {
		static displayName = `withExpose(${Component.displayName || Component.name})`;

		static defaultProps = {
			in: undefined,
			isCardTree: false,
			exposeDuration: 0,
			exposeEffect: '',
			children: '',
			onTransitionEnd: _.noop,
			editableModeProps: {},
		};

		childrenTransitions: any;

		constructor(props, context) {
			super(props, context);

			this.childrenTransitions = this.resetChildrenTransition();
		}

		componentDidUpdate(prevProps) {
			if (this.props.in !== prevProps.in) {
				this.childrenTransitions = this.resetChildrenTransition();
				if (!_.get(this.props, 'exposeEffect') && !_.size(this.childrenTransitions)) {
					_.invoke(this.props, 'onTransitionEnd', this.props);
				}
			}
		}

		onChildTransitionEnd = ({ _id, type }) => {
			if (this.childrenTransitions[_id]) {
				return;
			}

			this.childrenTransitions[_id] = true;

			if (_.values(this.childrenTransitions).every(isComplete => !!isComplete)) {
				// @ts-expect-error ts-migrate FIXME
				this.props.onTransitionEnd(this.props);
			}
		};

		get isEditableMode() {
			return isEditableMode(this.props);
		}

		get transitionCSS() {
			const { type, in: inProp } = this.props;
			const expose = this.props.exposeEffect || TRANSITION_TYPE.FADE;
			const preset = _.merge(
				{},
				_.get(TRANSITION_PRESET, ['common', expose]),
				_.get(TRANSITION_PRESET, [type, expose])
			);
			const transitionBaseCSS = preset.base;
			const transitionTypeCSS = preset[inProp ? 'in' : 'out'];
			const transitionCSS = { [transitionBaseCSS]: true, [transitionTypeCSS]: inProp !== undefined };

			return !this.isEditableMode && this.props.isCardTree ? transitionCSS : null;
		}

		resetChildrenTransition = () => {
			return _.reduce(
				// @ts-expect-error ts-migrate FIXME
				this.props.children,
				(acc, child) => {
					// @ts-expect-error ts-migrate FIXME
					if (_.get(child, 'props._id')) acc[child.props._id] = false;
					return acc;
				},
				{}
			);
		};

		renderChildren(): ComponentType | any {
			const { children } = this.props;
			const props = { onTransitionEnd: this.onChildTransitionEnd };

			if (typeof children !== 'string') {
				// @ts-expect-error ts-migrate FIXME
				return React.Children.map(children, child => React.cloneElement(child, props));
			}

			return children;
		}

		render() {
			const props = {
				...this.props,
				transitionCSS: this.transitionCSS,
				resetChildrenTransition: this.resetChildrenTransition,
			};

			/* @ts-expect-error ts-migrate FIXME */
			return <Block {...props}>{this.renderChildren()}</Block>;
		}
	};
};

export default function withCardTransitionContext<P>(Component: React.ComponentType<P & TransitionHandlerContextType>) {
	type Props = {
		_id: string;
		type: ComponentTypes;
		uiConfig: BBUiConfig;
		editableModeProps?: BBEditableModeProps;
		isCardTree: boolean;
		symbol?: BBSymbolInfo;
	};

	const Block = withExpose(Component);

	function WithCardTransitionContext(props: Omit<P & Props, keyof TransitionHandlerContextType>) {
		if (isLayerType(props).global && props.isCardTree) {
			/*
			 Global elements are rendered by StoryElements component where isCardTree is false.
			 In card tree stored only references to global elements. We should not to render them.
			 References are stored there to help to manage theirs z-index related to card.
			 */
			return null;
		}

		return (
			<>
				{isEditableMode(props) && props.editableModeProps && (
					<SelectedComponentWatcher
						_id={props._id}
						type={props.type}
						uiConfig={props.uiConfig}
						editableModeProps={props.editableModeProps}
						symbol={props.symbol}
					/>
				)}
				<CardTransitionContext.Consumer>
					{context => <Block in={context.in} onTransitionEnd={context.onTransitionEnd} {...props} />}
				</CardTransitionContext.Consumer>
			</>
		);
	}

	WithCardTransitionContext.displayName = `WithCardTransitionContext(${Component.displayName || Component.name})`;

	return WithCardTransitionContext;
}
